<script setup lang="ts">
/**
 * Onboarding tour — step 4 body (M4C-17).
 *
 * "100 fragments make a star": a progression illustration — a 5×5 grid of cyan
 * fragments → a large gold star → a faint constellation, joined by `→` arrows —
 * above a two-line heading and a Body-Strong line. The whole illustration is a
 * single hand-authored, hex-free inline SVG (colored through the `fragment`,
 * `star` and `fontColor` tokens) so it carries no expiring Figma asset URLs and
 * scales cleanly from the 532px desktop card down to the mobile card. The chrome
 * (progress header, footer nav, border, backdrop) is owned by the tour shell
 * (M4C-13); this component renders body content only.
 */
const { t } = useI18n();

/** GamiFragment atom geometry (28-unit viewBox) scaled to a 16px grid glyph. */
const FRAGMENT_SCALE = 16 / 28;

/**
 * 5×5 fragment grid laid out in the 532×96 illustration space: 16px glyphs on a
 * 20px pitch (16px + 4px gap), filling the leading 96×96 block.
 */
const gridCells = Array.from({ length: 25 }, (_, i) => ({
  x: (i % 5) * 20,
  y: Math.floor(i / 5) * 20,
}));

/**
 * Constellation (Lyra) dot centres, traced from the Figma node in the same
 * 532×96 space. The `gold` pair is the active star joined by the cyan link; the
 * `grey` dots and the `sparkle` are the faint surrounding stars.
 */
const constellationDots = [
  { x: 364, y: 62, variant: 'gold' },
  { x: 393, y: 89, variant: 'gold' },
  { x: 430, y: 25, variant: 'grey' },
  { x: 464, y: 41, variant: 'grey' },
  { x: 520, y: 53, variant: 'grey' },
  { x: 483, y: 6, variant: 'sparkle' },
] as const;

/** 5-point star nested in each constellation dot (20-unit viewBox, centred 10,10). */
const DOT_STAR =
  'M9.54394 14.2061L8.55469 12.0166L5.96289 11.8252L4.67969 11.7305L5.69043 10.9336L7.63477 9.40234L7.04394 7.125L6.74902 5.98926L7.77246 6.56445L10 7.81641L12.2275 6.56445L13.251 5.98926L12.9561 7.125L12.3662 9.40234L14.3096 10.9336L15.3203 11.7305L14.0371 11.8252L11.4463 12.0166L10.4561 14.2061L10 15.2139L9.54394 14.2061Z';
/** 4-point sparkle nested in the faint constellation dot (20-unit viewBox). */
const DOT_SPARKLE =
  'M10 16L8.85195 12.7716L5.75736 14.2426L7.22836 11.1481L4 10L7.22836 8.85195L5.75736 5.75736L8.85195 7.22836L10 4L11.1481 7.22836L14.2426 5.75736L12.7716 8.85195L16 10L12.7716 11.1481L14.2426 14.2426L11.148 12.7716L10 16Z';

/** Centre the 20-unit dot art on its traced centre, rendered at 18px. */
function dotTransform(dot: { x: number; y: number }): string {
  return `translate(${dot.x} ${dot.y}) scale(0.9) translate(-10 -10)`;
}
</script>

<template>
  <div class="flex flex-col gap-16">
    <!-- Progression illustration: fragment grid → gold star → constellation -->
    <div class="w-full">
      <svg
        viewBox="0 0 532 96"
        fill="none"
        aria-hidden="true"
        xmlns="http://www.w3.org/2000/svg"
        class="block h-auto w-full max-w-[532px]"
      >
        <defs>
          <!-- Reusable cyan fragment sparkle (mirrors the GamiFragment atom). -->
          <g id="m4c-step4-fragment">
            <path
              class="fill-fragment"
              opacity="0.3"
              transform="rotate(45 14 14)"
              d="M14 0L17.7813 10.2187L28 14L17.7813 17.7813L14 28L10.2187 17.7813L0 14L10.2187 10.2187L14 0Z"
            />
            <path
              class="fill-fragment"
              stroke="white"
              stroke-width="2.33333"
              d="M16.6875 10.624L16.873 11.127L17.376 11.3125L24.6387 14L17.376 16.6875L16.873 16.873L16.6875 17.376L14 24.6387L11.3125 17.376L11.127 16.873L10.624 16.6875L3.36035 14L10.624 11.3125L11.127 11.127L11.3125 10.624L14 3.36035L16.6875 10.624Z"
            />
          </g>
        </defs>

        <!-- 5×5 fragment grid -->
        <use
          v-for="(cell, index) in gridCells"
          :key="`fragment-${index}`"
          href="#m4c-step4-fragment"
          :transform="`translate(${cell.x} ${cell.y}) scale(${FRAGMENT_SCALE})`"
        />

        <!-- Arrows between each stage -->
        <g
          class="stroke-fontColor"
          fill="none"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <path d="M139.5 48 H152.5 M147.5 43.5 L152.5 48 L147.5 52.5" />
          <path d="M295.5 48 H308.5 M303.5 43.5 L308.5 48 L303.5 52.5" />
        </g>

        <!-- Large gold star -->
        <path
          class="fill-star"
          stroke="white"
          stroke-width="4.66667"
          transform="translate(196 22.1) scale(0.702)"
          d="M42.029 10.8534L49.2575 27.6064L68.0661 29.0595L73.8298 29.5048L69.3718 33.1855L55.1042 44.9648L59.4567 62.5507L60.7897 67.9374L56.0075 65.122L39.8864 55.6308L23.7653 65.122L18.9831 67.9374L20.3161 62.5507L24.6677 44.9648L10.4011 33.1855L5.94305 29.5048L11.7067 29.0595L30.5143 27.6064L37.7438 10.8534L39.8864 5.88957L42.029 10.8534Z"
        />

        <!-- Constellation: faint dashed lines, then the active cyan link, then dots -->
        <g
          class="stroke-fontColor-w90"
          fill="none"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-dasharray="4 4"
        >
          <path d="M364 62 L430 25 L464 41" />
          <path d="M464 41 L520 53 L483 6 Z" />
          <path d="M464 41 L393 89" />
        </g>
        <path
          class="stroke-fragment"
          fill="none"
          stroke-width="2"
          stroke-linecap="round"
          d="M364 62 L393 89"
        />
        <g
          v-for="(dot, index) in constellationDots"
          :key="`dot-${index}`"
          :transform="dotTransform(dot)"
        >
          <template v-if="dot.variant === 'sparkle'">
            <circle cx="10" cy="10" r="9.5" class="fill-fontColor-w80" stroke="white" />
            <path :d="DOT_SPARKLE" class="fill-white" />
          </template>
          <template v-else>
            <circle
              cx="10"
              cy="10"
              r="9.5"
              :class="dot.variant === 'gold' ? 'fill-star' : 'fill-fontColor-w80'"
              stroke="white"
            />
            <path
              :d="DOT_STAR"
              :class="dot.variant === 'gold' ? 'fill-star' : 'fill-fontColor-w80'"
              stroke="white"
              stroke-width="1"
            />
          </template>
        </g>
      </svg>
    </div>

    <!-- Heading (two lines) + body copy (24px gap) -->
    <div class="flex flex-col gap-6">
      <div>
        <p class="text-h1">{{ t('onboarding.tour.step4.heading_line1') }}</p>
        <p class="text-h1">{{ t('onboarding.tour.step4.heading_line2') }}</p>
      </div>
      <p class="text-body-strong">{{ t('onboarding.tour.step4.body') }}</p>
    </div>
  </div>
</template>
